import type { UserResponse } from 'clients/UserClient/types';

import { useMutation } from '@tanstack/react-query';
import { UserFeatureToggle } from 'clients/UserClient/types';
import { userClient } from 'clients/UserClient/UserClient';
import { queryClient } from 'config/queryClient';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { QueryKey } from 'types/query';

export const useUpdateCurrentUserFeatureTogglesMutation = () => {
  const { data: currentUser } = useCurrentUserQuery();

  return useMutation(
    async (userFeatureToggles: Partial<UserFeatureToggle>[]) => {
      const existingFeatures = currentUser?.features ?? [];

      const hasChanged = userFeatureToggles.some((toggle) => {
        const existingToggle = existingFeatures.find((f) => f.name === toggle.name);
        if (!existingToggle) return true;
        if (existingToggle.value !== toggle.value) return true;
        if (existingToggle.enabled !== toggle.enabled && toggle.enabled !== undefined) return true;
        return false;
      });

      if (!hasChanged) {
        return currentUser;
      }

      const newFeatures = existingFeatures
        .filter((f) => !userFeatureToggles.some((uf) => uf.name === f.name))
        .concat(userFeatureToggles as UserFeatureToggle[]);

      const { data } = await userClient.updateCurrentUser({ features: newFeatures });

      return data;
    },
    {
      onSuccess: (userResponse) => {
        if (!userResponse) return;
        queryClient.setQueryData<UserResponse>([QueryKey.useCurrentUserQuery], (currentData) => {
          if (!currentData) return currentData;

          return { ...currentData, features: userResponse.features };
        });
      },
    },
  );
};
